import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
    CheckCircle, Loader2, RefreshCw, Search,
    Star, MapPin, Unplug, ExternalLink
} from 'lucide-react';

function GoogleIcon({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
    );
}

export default function GoogleBusinessConnect() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [showSearch, setShowSearch] = useState(false);

    useEffect(() => { loadStatus(); }, []);

    const loadStatus = async () => {
        try {
            const res = await api.get('/google-business/status');
            setStatus(res.data);
        } catch {
            setStatus({ connected: false });
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = useCallback(async () => {
        if (searchQuery.length < 3) return;
        setSearching(true);
        setSearchResults([]);
        try {
            const res = await api.get('/google-business/search', { params: { q: searchQuery } });
            setSearchResults(res.data.results || []);
            if ((res.data.results || []).length === 0) {
                toast.info('Aucun résultat trouvé. Essayez un autre nom ou ajoutez la ville.');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur de recherche');
        } finally {
            setSearching(false);
        }
    }, [searchQuery]);

    const handleConnect = async (place) => {
        setConnecting(true);
        try {
            await api.post('/google-business/connect', {
                place_id: place.place_id,
                place_name: place.name,
                google_maps_url: place.google_maps_url
            });
            toast.success(`"${place.name}" connecté ! Avis synchronisés.`);
            setShowSearch(false);
            setSearchResults([]);
            setSearchQuery('');
            await loadStatus();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur de connexion');
        } finally {
            setConnecting(false);
        }
    };

    const handleDisconnect = async () => {
        if (!window.confirm('Supprimer la connexion Google ? Les avis ne seront plus affichés.')) return;
        try {
            await api.post('/google-business/disconnect');
            setStatus({ connected: false });
            setShowSearch(false);
            toast.success('Avis Google déconnectés');
        } catch {
            toast.error('Erreur de déconnexion');
        }
    };

    const handleSync = async () => {
        setSyncing(true);
        try {
            await api.post('/google-business/sync');
            await loadStatus();
            toast.success('Avis synchronisés');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur de synchronisation');
        } finally {
            setSyncing(false);
        }
    };

    if (loading) {
        return <div className="animate-pulse bg-card rounded-2xl h-32 border border-border" />;
    }

    return (
        <div className="bg-card rounded-2xl border border-border overflow-hidden" data-testid="gbp-connect-section">
            {/* Header */}
            <div className="p-6 border-b border-border">
                <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                    <GoogleIcon className="w-5 h-5" />
                    Avis Google
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                    Affichez les avis Google de votre agence sur votre profil Homly
                </p>
            </div>

            <div className="p-6">
                <AnimatePresence mode="wait">
                    {/* Connected state */}
                    {status?.connected ? (
                        <motion.div key="connected" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                            <div className="flex items-center gap-3 p-3 rounded-xl bg-green-500/10 border border-green-500/20">
                                <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-green-400">Connecté</p>
                                    <p className="text-xs text-muted-foreground truncate">{status.place_name}</p>
                                </div>
                                <div className="flex gap-1 shrink-0">
                                    <Button variant="ghost" size="sm" onClick={handleSync} disabled={syncing} className="h-7 text-xs text-muted-foreground" data-testid="gbp-sync-btn">
                                        <RefreshCw className={`w-3 h-3 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                                        Sync
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={handleDisconnect} className="h-7 text-xs text-red-400 hover:text-red-300" data-testid="gbp-disconnect-btn">
                                        <Unplug className="w-3 h-3 mr-1" />
                                    </Button>
                                </div>
                            </div>

                            {/* Stats */}
                            {status.avg_rating > 0 && (
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-1.5">
                                        <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                                        <span className="text-xl font-bold text-foreground">{status.avg_rating?.toFixed(1)}</span>
                                    </div>
                                    <span className="text-xs text-muted-foreground">{status.total_count} avis Google</span>
                                    {status.synced_at && (
                                        <span className="text-[10px] text-white/20">
                                            Dernière sync : {new Date(status.synced_at).toLocaleDateString('fr-FR')}
                                        </span>
                                    )}
                                </div>
                            )}

                            {status.google_maps_url && (
                                <a href={status.google_maps_url} target="_blank" rel="noopener noreferrer"
                                   className="inline-flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300">
                                    <ExternalLink className="w-3 h-3" />
                                    Voir sur Google Maps
                                </a>
                            )}
                        </motion.div>
                    ) : (
                        /* Not connected */
                        <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                            {!showSearch ? (
                                <div className="text-center py-4">
                                    <GoogleIcon className="w-12 h-12 mx-auto mb-3 opacity-40" />
                                    <p className="text-sm text-muted-foreground mb-4">
                                        Recherchez votre agence sur Google pour importer vos avis automatiquement.
                                    </p>
                                    <Button
                                        onClick={() => setShowSearch(true)}
                                        className="bg-white text-gray-800 hover:bg-gray-100 font-medium"
                                        data-testid="gbp-connect-btn"
                                    >
                                        <Search className="w-4 h-4 mr-2" />
                                        Rechercher mon agence
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {/* Search bar */}
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                            <input
                                                type="text"
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                                placeholder="Ex: Agence Dupont Immobilier Paris"
                                                className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#681793]/50"
                                                data-testid="gbp-search-input"
                                                autoFocus
                                            />
                                        </div>
                                        <Button onClick={handleSearch} disabled={searching || searchQuery.length < 3} size="sm" data-testid="gbp-search-btn">
                                            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Rechercher'}
                                        </Button>
                                    </div>
                                    <p className="text-[10px] text-muted-foreground">
                                        Tapez le nom de votre agence tel qu'il apparait sur Google Maps, suivi de la ville.
                                    </p>

                                    {/* Results */}
                                    {searching && (
                                        <div className="text-center py-6">
                                            <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                                        </div>
                                    )}

                                    {!searching && searchResults.length > 0 && (
                                        <div className="space-y-2 max-h-72 overflow-y-auto">
                                            {searchResults.map((place) => (
                                                <button
                                                    key={place.place_id}
                                                    onClick={() => handleConnect(place)}
                                                    disabled={connecting}
                                                    className="w-full flex items-start gap-3 p-3 rounded-xl border border-border hover:border-green-500/40 hover:bg-green-500/5 transition-all text-left"
                                                    data-testid="gbp-search-result"
                                                >
                                                    <MapPin className="w-4 h-4 text-[#FF914D] shrink-0 mt-0.5" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-foreground truncate">{place.name}</p>
                                                        <p className="text-[10px] text-muted-foreground truncate">{place.address}</p>
                                                        {place.rating > 0 && (
                                                            <div className="flex items-center gap-1 mt-1">
                                                                <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                                                                <span className="text-[10px] text-foreground">{place.rating}</span>
                                                                <span className="text-[10px] text-muted-foreground">({place.review_count} avis)</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <CheckCircle className="w-4 h-4 text-green-400/50 shrink-0 mt-0.5" />
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    <button onClick={() => { setShowSearch(false); setSearchResults([]); setSearchQuery(''); }}
                                        className="text-xs text-muted-foreground hover:text-foreground">
                                        Annuler
                                    </button>
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}
